import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Crown, Home, Trophy, Target, Award, User, LayoutDashboard, Users, ShieldCheck, LogOut, KeyRound, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { useConfig } from "../context/ConfigContext";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationsBell from "./NotificationsBell";
import Avatar from "./Avatar";
import { Modal } from "./common";
import { wsLabel } from "../utils";
import api, { apiError } from "../api";
import { toast } from "sonner";

const LABEL_OVERRIDE = { "nav.ranking": "leaderboard", "nav.challenges": "challenges", "nav.badges": "badges", "nav.students": "students" };

const NAV = {
  student: [
    { to: "/", icon: Home, key: "nav.home", end: true },
    { to: "/ranking", icon: Trophy, key: "nav.ranking" },
    { to: "/challenges", icon: Target, key: "nav.challenges" },
    { to: "/badges", icon: Award, key: "nav.badges" },
    { to: "/profile", icon: User, key: "nav.profile" },
  ],
  teacher: [
    { to: "/teacher", icon: LayoutDashboard, key: "nav.dashboard", end: true },
    { to: "/teacher/students", icon: Users, key: "nav.students" },
    { to: "/teacher/challenges", icon: Target, key: "nav.challenges" },
    { to: "/ranking", icon: Trophy, key: "nav.ranking" },
  ],
  admin: [
    { to: "/admin", icon: ShieldCheck, key: "nav.admin_dashboard", end: true },
    { to: "/teacher/students", icon: Users, key: "nav.students" },
    { to: "/ranking", icon: Trophy, key: "nav.ranking" },
  ],
  super_admin: [
    { to: "/admin", icon: ShieldCheck, key: "nav.admin_dashboard", end: true },
    { to: "/teacher/students", icon: Users, key: "nav.students" },
    { to: "/ranking", icon: Trophy, key: "nav.ranking" },
  ],
};

function ChangePasswordModal({ open, onClose }) {
  const { t } = useLang();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next.length < 6) { toast.error(t("validation.min_6")); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(t("auth.password_changed"));
      onClose();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={t("auth.change_password")} testId="change-password-modal">
      <div className="space-y-3">
        <input data-testid="current-password-input" type="password" className="input-magic" placeholder={t("auth.current_password")} value={cur} onChange={(e) => setCur(e.target.value)} />
        <input data-testid="new-password-input" type="password" className="input-magic" placeholder={t("auth.new_password")} value={next} onChange={(e) => setNext(e.target.value)} />
        <button data-testid="change-password-submit" disabled={busy || !cur || !next} onClick={submit} className="btn-magic w-full">{t("common.confirm")}</button>
      </div>
    </Modal>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, pick, lang } = useLang();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const items = NAV[user?.role] || NAV.student;
  const settings = config?.settings || {};
  const navLabel = (key) => wsLabel(config?.ui_labels, LABEL_OVERRIDE[key], lang, t(key));

  return (
    <div className="min-h-screen bg-[#08082C] bg-stardust relative">
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 start-[-10%] w-[34rem] h-[34rem] rounded-full bg-[#7625D8]/18 blur-[120px]" />
        <div className="absolute top-1/3 end-[-12%] w-[30rem] h-[30rem] rounded-full bg-[#EF4D9A]/12 blur-[120px]" />
        <div className="absolute bottom-[-15%] start-[30%] w-[28rem] h-[28rem] rounded-full bg-[#268CEB]/12 blur-[120px]" />
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#10103D]/85 border-b border-white/8">
        <div className="max-w-[1440px] mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FFAE32] to-[#FFC83D] flex items-center justify-center glow-gold shrink-0 animate-floaty">
              <Crown className="w-6 h-6 text-[#3A2400]" fill="currentColor" />
            </div>
            <div className="min-w-0">
              <h1 data-testid="app-title" className="font-black text-base sm:text-xl leading-tight truncate bg-gradient-to-r from-[#FFC83D] via-[#FF63B5] to-[#9C42F5] bg-clip-text text-transparent">
                {pick(settings, "journey_title") || t("dash.excellence_stages")}
              </h1>
              <p data-testid="term-label" className="text-[11px] sm:text-xs text-[#A5A0D6] font-semibold truncate">
                {pick(settings, "term_name")}
              </p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1 mx-auto">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`nav-${it.key.split(".")[1]}`}
                className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-bold transition-colors ${isActive ? "bg-gradient-to-r from-[#7625D8] to-[#9C42F5] text-white glow-purple" : "text-[#A5A0D6] hover:text-white hover:bg-white/6"}`}>
                {navLabel(it.key)}
              </NavLink>
            ))}
          </nav>
          <div className="ms-auto md:ms-0 flex items-center gap-2">
            <LanguageSwitcher />
            <NotificationsBell />
            <div className="relative">
              <button data-testid="user-menu-btn" onClick={() => setMenuOpen(!menuOpen)} aria-label={t("nav.profile")}>
                <Avatar src={user?.avatar} name={user ? pick(user, "name") : "?"} size="sm" />
              </button>
              {menuOpen && (
                <div data-testid="user-menu" className="absolute end-0 top-14 w-56 magic-panel p-2 z-50 animate-pop">
                  <div className="px-3 py-2 border-b border-white/8 mb-1">
                    <p className="font-extrabold text-sm">{pick(user, "name")}</p>
                    <p className="text-[11px] text-[#A5A0D6]">{user?.username}</p>
                  </div>
                  <button data-testid="change-password-btn" onClick={() => { setMenuOpen(false); setPwOpen(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-white/8 transition-colors">
                    <KeyRound className="w-4 h-4 text-[#27C9EE]" /> {t("auth.change_password")}
                  </button>
                  <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold text-[#FF8FB1] hover:bg-white/8 transition-colors">
                    <LogOut className="w-4 h-4" /> {t("auth.logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="relative max-w-[1440px] mx-auto px-3 sm:px-4 pt-8 pb-28 md:pb-12">
        <Outlet />
        <footer className="mt-10 text-center">
          <p data-testid="footer-motivation" className="inline-flex items-center gap-2 text-sm font-bold text-[#D8D4F2]">
            <Heart className="w-4 h-4 text-[#FF63B5]" fill="currentColor" />
            {pick(settings, "motivation") || "كل إنجاز صغير يقودك لنجاح كبير 💗✨"}
          </p>
        </footer>
      </main>

      <nav data-testid="bottom-nav" className="md:hidden fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-[#10103D]/92 border-t border-white/10">
        <div className="flex justify-around py-2">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={`bottom-nav-${it.key.split(".")[1]}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 px-3 py-1.5 rounded-2xl min-w-[3.4rem] transition-colors ${isActive ? "text-white" : "text-[#A5A0D6]"}`}>
              {({ isActive }) => (
                <>
                  <span className={`w-10 h-8 rounded-xl flex items-center justify-center transition-all ${isActive ? "bg-gradient-to-r from-[#7625D8] to-[#9C42F5] glow-purple" : ""}`}>
                    <it.icon className="w-5 h-5" />
                  </span>
                  <span className="text-[10px] font-bold">{navLabel(it.key)}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
